import { useState, useEffect, useRef } from 'react'
import { Wand2, ChevronDown, ChevronRight, RotateCcw, Download, ClipboardPaste, Copy, Check, Play, Square, Loader2, ExternalLink } from 'lucide-react'
import * as XLSX from 'xlsx'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { api } from '@/api/client'
import type { ClientProject, SequenceDiagramEntry } from '@/api/client'
import type { GeneratedTestStep, StepRunResult, CaseRunState } from './testCaseRunner'
import { loadCredentials, executeStep, extractEntityId } from './testCaseRunner'

// ── Types ──────────────────────────────────────────────────────────────────────

interface GeneratedTestCase {
  id: string
  scenario: string
  documentType: string
  expectedResult: 'CLEAR' | 'FAIL' | 'REFER' | 'ASYNC' | 'PASS'
  steps: GeneratedTestStep[]
  notes?: string
}

type ActualResult = 'PASS' | 'FAIL' | 'BLOCKED' | 'SKIP'

interface TestCaseResult {
  actual: ActualResult | null
  notes: string
}

// ── Constants ──────────────────────────────────────────────────────────────────

const EXPECTED_RESULT_STYLES: Record<string, string> = {
  CLEAR:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  PASS:   'bg-green-500/15 text-green-400 border-green-500/30',
  FAIL:   'bg-red-500/15 text-red-400 border-red-500/30',
  REFER:  'bg-amber-500/15 text-amber-400 border-amber-500/30',
  ASYNC:  'bg-blue-500/15 text-blue-400 border-blue-500/30',
}

const ACTUAL_RESULT_OPTIONS: { value: ActualResult; label: string; style: string }[] = [
  { value: 'PASS',    label: 'Pass',    style: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30' },
  { value: 'FAIL',    label: 'Fail',    style: 'bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30' },
  { value: 'BLOCKED', label: 'Blocked', style: 'bg-amber-500/20 text-amber-400 border-amber-500/40 hover:bg-amber-500/30' },
  { value: 'SKIP',    label: 'Skip',    style: 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]' },
]

// ── Helpers ────────────────────────────────────────────────────────────────────

function storageKey(clientId: number, diagramId: number): string {
  return `tcg-results-${clientId}-${diagramId}`
}

function loadResults(clientId: number, diagramId: number): Record<string, TestCaseResult> {
  try {
    const raw = localStorage.getItem(storageKey(clientId, diagramId))
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function saveResults(clientId: number, diagramId: number, results: Record<string, TestCaseResult>): void {
  try {
    localStorage.setItem(storageKey(clientId, diagramId), JSON.stringify(results))
  } catch { /* ignore */ }
}

function progressSummary(cases: GeneratedTestCase[], results: Record<string, TestCaseResult>): { pass: number; fail: number; blocked: number; skip: number; pending: number } {
  const summary = { pass: 0, fail: 0, blocked: 0, skip: 0, pending: 0 }
  for (const tc of cases) {
    const r = results[tc.id]?.actual
    if (r === 'PASS') summary.pass++
    else if (r === 'FAIL') summary.fail++
    else if (r === 'BLOCKED') summary.blocked++
    else if (r === 'SKIP') summary.skip++
    else summary.pending++
  }
  return summary
}

function buildPrompt(clientName: string, dsl: string): string {
  return `You are a QA engineer helping generate UAT test cases for a FrankieOne identity verification integration.

Client: ${clientName}

Below is the sequence diagram DSL describing the API flow this client uses:

---
${dsl}
---

Generate a comprehensive set of UAT test cases for this client's integration based on the sequence diagram above.

Return ONLY a valid JSON array of test case objects. No markdown, no explanation, no code fences — just the raw JSON array.

Each object must have exactly these fields:
- "id": string — sequential ID like "TC-01", "TC-02"
- "scenario": string — short description of what is being tested
- "documentType": string — document used in this test ("Driver's Licence", "Passport", "Medicare", "Foreign Passport", "N/A")
- "expectedResult": one of "CLEAR", "FAIL", "REFER", "ASYNC", "PASS"
- "steps": array of step objects, each with:
    - "stepType": one of "api", "hosted", "webhook_wait", "manual"
        "api" = direct FrankieOne API call (include endpoint + payload)
        "hosted" = generates a OneSDK hosted URL the user opens in a browser
        "webhook_wait" = waits for a webhook event to arrive after an async operation
        "manual" = any step requiring human interaction (portal login, form filling, SDK interaction)
    - "action": string — human-readable description of what this step does
    - "endpoint": string (required for api and hosted steps) — format "METHOD /path" e.g. "POST /individuals" (do NOT include /v2 prefix)
    - "payload": object (optional) — the JSON body to send as a JSON object (NOT a string), only for api and hosted steps
    - "assertion": string (optional) — what to verify, e.g. "status 200", "entityId present", "workflowStatus == COMPLETE"
- "notes": string (optional) — any important notes for the tester

Important stepType rules:
- Always add a "webhook_wait" step after any async workflow execution (KYB, async KYC)
- Use "hosted" for any OneSDK hosted URL generation step
- Use "manual" for any step requiring browser interaction (embedded SDK, portal actions, form filling, liveness capture)
- Every "api" and "hosted" step must have an "endpoint"

Include test cases covering:
1. Happy path for each supported document type (use JAMES A TESTONE / DOB 1950-01-01 persona for CLEAR results)
2. FAIL scenarios: no records found (JENNY TESTEIGHT persona), name mismatch, DOB mismatch
3. AML scenarios if the diagram includes AML checks: PEP hit (REFER), Sanctions hit (FAIL)
4. Re-run existing entity (idempotency)
5. Any other scenarios shown in the sequence diagram

Use realistic FrankieOne UAT API payloads. For TESTONE: givenName JAMES, middleName A, familyName TESTONE, DOB 1950-01-01, address U 1/35 CONN STREET FERNTREE GULLY VIC 3156.
For TESTEIGHT: givenName JENNY, familyName TESTEIGHT, DOB 1981-08-08, address 1 TEST STREET SYDNEY NSW 2000.

Return only the JSON array.`
}

function exportToExcel(clientName: string, cases: GeneratedTestCase[], results: Record<string, TestCaseResult>): void {
  const rows = cases.map(tc => ({
    ID: tc.id,
    Scenario: tc.scenario,
    'Document Type': tc.documentType,
    'Expected Result': tc.expectedResult,
    'Actual Result': results[tc.id]?.actual ?? '',
    'Tester Notes': results[tc.id]?.notes ?? '',
    Steps: tc.steps.map((s, i) => `${i + 1}. ${s.action}${s.payload ? `\n   Payload: ${JSON.stringify(s.payload)}` : ''}${s.assertion ? `\n   Assert: ${s.assertion}` : ''}`).join('\n\n'),
    'AI Notes': tc.notes ?? '',
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  ws['!cols'] = [{ wch: 8 }, { wch: 40 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 30 }, { wch: 80 }, { wch: 40 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'UAT Test Cases')
  XLSX.writeFile(wb, `${clientName.replace(/\s+/g, '_')}_UAT_Test_Cases.xlsx`)
}

// ── TestCaseRow ────────────────────────────────────────────────────────────────

function TestCaseRow({
  tc,
  idx,
  result,
  onResultChange,
  onNotesChange,
  runState,
  isAnyRunning,
  onRun,
  onStop,
  manualWaitStepIdx,
  onManualDone,
}: {
  tc: GeneratedTestCase
  idx: number
  result: TestCaseResult
  onResultChange: (actual: ActualResult | null) => void
  onNotesChange: (notes: string) => void
  runState?: CaseRunState
  isAnyRunning: boolean
  onRun: () => void
  onStop: () => void
  manualWaitStepIdx?: number
  onManualDone?: () => void
}) {
  const [open, setOpen] = useState(false)
  const isRowRunning = runState?.status === 'running'
  const effectiveOpen = open || isRowRunning

  // Auto-open row when run completes so results are visible
  useEffect(() => {
    if (runState?.status === 'pass' || runState?.status === 'fail' || runState?.status === 'error') {
      setOpen(true)
    }
  }, [runState?.status])

  const stepTypeColors: Record<string, string> = {
    api: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    hosted: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    webhook_wait: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    manual: 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)]',
  }

  return (
    <>
      <tr
        className={`border-t border-[var(--border-color)] cursor-pointer transition-colors ${
          effectiveOpen
            ? 'bg-[var(--bg-tertiary)]'
            : idx % 2 === 0
            ? 'bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)]'
            : 'bg-[var(--bg-secondary)]/60 hover:bg-[var(--bg-tertiary)]'
        }`}
        onClick={() => setOpen(o => !o)}
      >
        <td className="px-3 py-2 w-4">
          {effectiveOpen
            ? <ChevronDown size={12} className="text-[var(--text-muted)]" />
            : <ChevronRight size={12} className="text-[var(--text-muted)]" />}
        </td>
        <td className="px-2 py-2 text-[10px] font-mono text-[var(--text-muted)] w-12">{tc.id}</td>
        <td className="px-2 py-2 text-xs text-[var(--text-primary)] font-medium">{tc.scenario}</td>
        <td className="px-2 py-2 text-[11px] text-[var(--text-muted)] hidden md:table-cell">{tc.documentType}</td>
        <td className="px-2 py-2">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${EXPECTED_RESULT_STYLES[tc.expectedResult] ?? ''}`}>
            {tc.expectedResult}
          </span>
        </td>
        <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1">
            {ACTUAL_RESULT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => onResultChange(result.actual === opt.value ? null : opt.value)}
                className={`px-1.5 py-0.5 rounded border text-[10px] font-bold transition-colors ${
                  result.actual === opt.value
                    ? opt.style
                    : 'border-transparent text-[var(--text-muted)] hover:border-[var(--border-color)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </td>
        <td className="px-2 py-2 w-16" onClick={e => e.stopPropagation()}>
          {isRowRunning ? (
            <button
              onClick={onStop}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-[10px] text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <Square size={9} /> Stop
            </button>
          ) : (
            <button
              onClick={onRun}
              disabled={isAnyRunning}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-[var(--border-color)] text-[10px] text-[var(--text-muted)] hover:border-violet-500/50 hover:text-violet-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Play size={9} /> Run
            </button>
          )}
        </td>
      </tr>

      {effectiveOpen && (
        <tr className="border-t border-[var(--border-color)]">
          <td colSpan={7} className="p-4 bg-[var(--bg-primary)]">
            <div className="space-y-4">
              <ol className="space-y-3">
                {tc.steps.map((step, i) => {
                  const stepResult = runState?.stepStatuses[i]
                  const isActive = runState?.activeStepIdx === i
                  return (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full border text-[10px] font-bold flex items-center justify-center mt-0.5 ${
                        stepResult?.status === 'pass' ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' :
                        stepResult?.status === 'fail' || stepResult?.status === 'error' ? 'bg-red-500/20 border-red-500/40 text-red-400' :
                        isActive ? 'bg-violet-500/20 border-violet-500/40 text-violet-400' :
                        'bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-muted)]'
                      }`}>
                        {stepResult?.status === 'pass' ? '✓' :
                         stepResult?.status === 'fail' || stepResult?.status === 'error' ? '✗' :
                         isActive ? <Loader2 size={9} className="animate-spin" /> :
                         i + 1}
                      </span>
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[11px] text-[var(--text-primary)]">{step.action}</p>
                          {step.stepType && (
                            <span className={`px-1 py-0 rounded border text-[9px] font-mono ${stepTypeColors[step.stepType] ?? stepTypeColors['manual']}`}>
                              {step.stepType}
                            </span>
                          )}
                          {step.endpoint && (
                            <span className="text-[9px] font-mono text-[var(--text-muted)]">{step.endpoint}</span>
                          )}
                        </div>
                        {step.payload && (
                          <pre className="text-[10px] text-[var(--text-muted)] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(step.payload, null, 2)}</pre>
                        )}
                        {step.assertion && (
                          <p className="text-[10px] text-emerald-400 italic">Assert: {step.assertion}</p>
                        )}
                        {stepResult?.errorMessage && (
                          <p className="text-[10px] text-red-400">Error: {stepResult.errorMessage}</p>
                        )}
                        {stepResult?.responseBody !== undefined && (
                          <details className="text-[10px]" open>
                            <summary className="cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)]">Response</summary>
                            <pre className="mt-1 text-[var(--text-muted)] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(stepResult.responseBody, null, 2)}</pre>
                          </details>
                        )}
                        {stepResult?.status === 'waiting' && step.stepType === 'hosted' && stepResult.hostedUrl && manualWaitStepIdx === i && (
                          <div className="space-y-1.5 p-2 rounded border border-violet-500/30 bg-violet-500/5">
                            <p className="text-[10px] text-violet-400 font-semibold">Open this URL in a browser and complete the flow:</p>
                            <a href={stepResult.hostedUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-violet-300 hover:text-violet-200 underline break-all">
                              <ExternalLink size={10} />{stepResult.hostedUrl}
                            </a>
                            <Button variant="secondary" size="sm" onClick={onManualDone}>
                              Mark Done — Continue
                            </Button>
                          </div>
                        )}
                        {stepResult?.status === 'waiting' && (step.stepType === 'manual' || !step.stepType) && manualWaitStepIdx === i && (
                          <div className="p-2 rounded border border-amber-500/30 bg-amber-500/5">
                            <p className="text-[10px] text-amber-400 mb-1.5">Complete this step manually, then continue:</p>
                            <Button variant="secondary" size="sm" onClick={onManualDone}>
                              Mark Done — Continue
                            </Button>
                          </div>
                        )}
                        {step.stepType === 'webhook_wait' && isActive && !stepResult?.webhookEvent && (
                          <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                            <Loader2 size={10} className="animate-spin" />
                            Waiting for webhook… {runState?.entityId && `(entity: ${runState.entityId})`}
                          </div>
                        )}
                        {step.stepType === 'webhook_wait' && stepResult?.webhookEvent !== undefined && (
                          <div className="p-2 rounded border border-emerald-500/30 bg-emerald-500/5 space-y-1">
                            <p className="text-[10px] text-emerald-400 font-semibold">✓ Webhook received</p>
                            <details className="text-[10px]">
                              <summary className="cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)]">Webhook payload</summary>
                              <pre className="mt-1 text-[var(--text-muted)] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(stepResult.webhookEvent, null, 2)}</pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ol>

              {tc.notes && (
                <p className="text-[10px] text-amber-400 italic">Note: {tc.notes}</p>
              )}

              <div>
                <label className="block text-[10px] text-[var(--text-muted)] mb-1">Tester notes</label>
                <textarea
                  value={result.notes}
                  onChange={e => onNotesChange(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  placeholder="Add notes about actual behavior, errors seen, etc."
                  rows={2}
                  className="w-full text-[11px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-violet-500/50"
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function TestCaseGenerator() {
  const [clients, setClients] = useState<ClientProject[]>([])
  const [diagrams, setDiagrams] = useState<SequenceDiagramEntry[]>([])
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
  const [selectedDiagramId, setSelectedDiagramId] = useState<number | null>(null)
  const [clientSearch, setClientSearch] = useState('')
  const [diagramSearch, setDiagramSearch] = useState('')
  const [clientFocused, setClientFocused] = useState(false)
  const [diagramFocused, setDiagramFocused] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [showPasteJson, setShowPasteJson] = useState(false)
  const [pasteJsonValue, setPasteJsonValue] = useState('')
  const [pasteJsonError, setPasteJsonError] = useState<string | null>(null)
  const [promptCopied, setPromptCopied] = useState(false)
  const [cases, setCases] = useState<GeneratedTestCase[]>([])
  const [results, setResults] = useState<Record<string, TestCaseResult>>({})
  const [error, setError] = useState<string | null>(null)

  // ── Runner state ─────────────────────────────────────────────────────────────
  const [runStates, setRunStates] = useState<Record<string, CaseRunState>>({})
  const [isRunning, setIsRunning] = useState(false)
  const [runAllProgress, setRunAllProgress] = useState<{ current: number; total: number } | null>(null)
  const [manualWait, setManualWait] = useState<{ caseId: string; stepIdx: number } | null>(null)
  const manualResolveRef = useRef<(() => void) | null>(null)
  const abortControllersRef = useRef<Record<string, AbortController>>({})

  useEffect(() => {
    api.clientProjects.list().then(r => { if (r.success && r.data) setClients(r.data) })
    api.sequenceDiagramLibrary.list().then(r => { if (r.success && r.data) setDiagrams(r.data) })
  }, [])

  useEffect(() => {
    if (selectedClientId && selectedDiagramId) {
      setResults(loadResults(selectedClientId, selectedDiagramId))
    }
  }, [selectedClientId, selectedDiagramId])

  function updateResult(tcId: string, actual: ActualResult | null) {
    if (!selectedClientId || !selectedDiagramId) return
    const next = { ...results, [tcId]: { actual, notes: results[tcId]?.notes ?? '' } }
    setResults(next)
    saveResults(selectedClientId, selectedDiagramId, next)
  }

  function updateNotes(tcId: string, notes: string) {
    if (!selectedClientId || !selectedDiagramId) return
    const next = { ...results, [tcId]: { actual: results[tcId]?.actual ?? null, notes } }
    setResults(next)
    saveResults(selectedClientId, selectedDiagramId, next)
  }

  function loadFromPaste() {
    setPasteJsonError(null)
    try {
      const cleaned = pasteJsonValue.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
      const parsed = JSON.parse(cleaned)
      const arr = Array.isArray(parsed) ? parsed : parsed.cases
      if (!Array.isArray(arr)) throw new Error('Expected a JSON array (or object with "cases" array)')
      setCases(arr)
      setPasteJsonValue('')
      setShowPasteJson(false)
    } catch (e) {
      setPasteJsonError(e instanceof Error ? e.message : 'Invalid JSON')
    }
  }

  async function generate() {
    if (!selectedClientId || !selectedDiagramId) return
    const client = clients.find(c => c.id === selectedClientId)
    const diagram = diagrams.find(d => d.id === selectedDiagramId)
    if (!client || !diagram) return

    setGenerating(true)
    setError(null)
    setCases([])

    try {
      const res = await fetch('/api/test-case-generator/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName: client.customerName, dsl: diagram.dsl }),
      })
      const data = await res.json() as { cases?: GeneratedTestCase[]; error?: string }
      if (!res.ok || data.error) {
        setError(data.error ?? 'Generation failed')
        return
      }
      setCases(data.cases ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error')
    } finally {
      setGenerating(false)
    }
  }

  async function runCase(tc: GeneratedTestCase) {
    const creds = loadCredentials()
    if (!creds) return

    const abort = new AbortController()
    abortControllersRef.current[tc.id] = abort
    setIsRunning(true)

    const initialStepStatuses: (StepRunResult | undefined)[] = tc.steps.map(() => undefined)
    setRunStates(prev => ({
      ...prev,
      [tc.id]: { status: 'running', stepStatuses: initialStepStatuses, activeStepIdx: null, entityId: null },
    }))

    let entityId: string | null = null

    for (let i = 0; i < tc.steps.length; i++) {
      if (abort.signal.aborted) break

      const step = tc.steps[i]
      setRunStates(prev => ({
        ...prev,
        [tc.id]: { ...prev[tc.id], activeStepIdx: i },
      }))

      let result: StepRunResult

      if (step.stepType === 'manual' || step.stepType === 'hosted' || !step.stepType) {
        // For hosted: first make the API call to get the URL, then pause for user
        const initialResult = await executeStep(step, entityId, creds, () => {}, abort.signal)
        setRunStates(prev => {
          const current = prev[tc.id]
          const stepStatuses = [...current.stepStatuses]
          stepStatuses[i] = initialResult
          return { ...prev, [tc.id]: { ...current, stepStatuses } }
        })
        setManualWait({ caseId: tc.id, stepIdx: i })
        await new Promise<void>(resolve => { manualResolveRef.current = resolve })
        manualResolveRef.current = null
        setManualWait(null)
        if (abort.signal.aborted) break
        result = { status: 'pass' }
      } else {
        result = await executeStep(
          step,
          entityId,
          creds,
          (event) => {
            setRunStates(prev => {
              const current = prev[tc.id]
              const stepStatuses = [...current.stepStatuses]
              stepStatuses[i] = { ...(stepStatuses[i] ?? {}), webhookEvent: event, status: 'pass' }
              return { ...prev, [tc.id]: { ...current, stepStatuses } }
            })
          },
          abort.signal,
        ).catch(e => ({ status: 'error' as const, errorMessage: e instanceof Error ? e.message : 'Error' }))
      }

      const extracted = extractEntityId(result.responseBody)
      if (extracted) entityId = extracted

      setRunStates(prev => {
        const current = prev[tc.id]
        const stepStatuses = [...current.stepStatuses]
        stepStatuses[i] = result
        return { ...prev, [tc.id]: { ...current, stepStatuses, entityId: entityId ?? current.entityId } }
      })

      if (result.status === 'fail' || result.status === 'error') {
        setRunStates(prev => ({
          ...prev,
          [tc.id]: { ...prev[tc.id], status: 'fail', activeStepIdx: null },
        }))
        updateResult(tc.id, 'FAIL')
        delete abortControllersRef.current[tc.id]
        if (Object.keys(abortControllersRef.current).length === 0) setIsRunning(false)
        return
      }
    }

    if (abort.signal.aborted) {
      setRunStates(prev => ({ ...prev, [tc.id]: { ...prev[tc.id], status: 'idle', activeStepIdx: null } }))
      delete abortControllersRef.current[tc.id]
      if (Object.keys(abortControllersRef.current).length === 0) setIsRunning(false)
      return
    }

    setRunStates(prev => ({ ...prev, [tc.id]: { ...prev[tc.id], status: 'pass', activeStepIdx: null } }))
    updateResult(tc.id, 'PASS')
    delete abortControllersRef.current[tc.id]
    if (Object.keys(abortControllersRef.current).length === 0) setIsRunning(false)
  }

  function stopCase(tcId: string) {
    abortControllersRef.current[tcId]?.abort()
    manualResolveRef.current?.()
    manualResolveRef.current = null
    setManualWait(null)
  }

  async function runAll() {
    const creds = loadCredentials()
    if (!creds) return
    const pending = cases.filter(tc => results[tc.id]?.actual !== 'PASS')
    setRunAllProgress({ current: 0, total: pending.length })
    for (let i = 0; i < pending.length; i++) {
      setRunAllProgress({ current: i + 1, total: pending.length })
      await runCase(pending[i])
    }
    setRunAllProgress(null)
  }

  function stopAll() {
    Object.values(abortControllersRef.current).forEach(ctrl => ctrl.abort())
    manualResolveRef.current?.()
    manualResolveRef.current = null
    setManualWait(null)
    setRunAllProgress(null)
    setIsRunning(false)
  }

  const selectedClient = clients.find(c => c.id === selectedClientId)
  const selectedDiagram = diagrams.find(d => d.id === selectedDiagramId)
  const canGenerate = selectedClientId !== null && selectedDiagramId !== null && !generating
  const filteredClients = clientSearch.trim()
    ? clients.filter(c => c.customerName.toLowerCase().includes(clientSearch.toLowerCase()))
    : clients
  const filteredDiagrams = diagramSearch.trim()
    ? diagrams.filter(d => d.name.toLowerCase().includes(diagramSearch.toLowerCase()))
    : diagrams
  const progress = cases.length > 0 ? progressSummary(cases, results) : null
  const credentials = loadCredentials()

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-500/10 flex-shrink-0">
          <Wand2 size={18} className="text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">UAT Test Case Generator</h2>
          <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
            Select a client and their API sequence diagram — AI generates a full UAT test case suite.
          </p>
        </div>
      </div>

      <Card className="p-3">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
          <div className="flex-1 min-w-0">
            <label className="block text-[10px] text-[var(--text-muted)] mb-1 uppercase tracking-wide">Client</label>
            {selectedClient ? (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-[var(--bg-secondary)] border border-violet-500/40 text-xs text-[var(--text-primary)]">
                <span className="flex-1 truncate">{selectedClient.customerName}</span>
                <button onClick={() => { setSelectedClientId(null); setClientSearch(''); setCases([]) }} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">✕</button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  onFocus={() => setClientFocused(true)}
                  onBlur={() => setTimeout(() => setClientFocused(false), 150)}
                  placeholder="Search clients…"
                  className="w-full text-xs bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/50"
                />
                {clientFocused && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.length === 0 ? (
                      <p className="px-3 py-2 text-[11px] text-[var(--text-muted)]">No clients found</p>
                    ) : filteredClients.map(c => (
                      <button
                        key={c.id}
                        onClick={() => { setSelectedClientId(c.id); setClientSearch(''); setCases([]) }}
                        className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] truncate"
                      >
                        {c.customerName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <label className="block text-[10px] text-[var(--text-muted)] mb-1 uppercase tracking-wide">Sequence Diagram</label>
            {selectedDiagram ? (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-[var(--bg-secondary)] border border-violet-500/40 text-xs text-[var(--text-primary)]">
                <span className="flex-1 truncate">{selectedDiagram.name}</span>
                <button onClick={() => { setSelectedDiagramId(null); setDiagramSearch(''); setCases([]) }} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">✕</button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={diagramSearch}
                  onChange={e => setDiagramSearch(e.target.value)}
                  onFocus={() => setDiagramFocused(true)}
                  onBlur={() => setTimeout(() => setDiagramFocused(false), 150)}
                  placeholder="Search diagrams…"
                  className="w-full text-xs bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/50"
                />
                {diagramFocused && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded shadow-lg max-h-48 overflow-y-auto">
                    {filteredDiagrams.length === 0 ? (
                      <p className="px-3 py-2 text-[11px] text-[var(--text-muted)]">No diagrams found</p>
                    ) : filteredDiagrams.map(d => (
                      <button
                        key={d.id}
                        onClick={() => { setSelectedDiagramId(d.id); setDiagramSearch(''); setCases([]) }}
                        className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] truncate"
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={generate}
            disabled={!canGenerate}
            className="flex-shrink-0 flex items-center gap-1.5"
          >
            <Wand2 size={13} />
            {generating ? 'Generating…' : 'Generate Test Cases'}
          </Button>
        </div>

        {selectedClient && selectedDiagram && (
          <p className="text-[10px] text-[var(--text-muted)] mt-2">
            {selectedClient.customerName} · {selectedDiagram.name}
          </p>
        )}
      </Card>

      {/* Manual testing — copy prompt + paste JSON response */}
      <div>
        <button
          onClick={() => setShowPasteJson(v => !v)}
          className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ClipboardPaste size={12} />
          {showPasteJson ? 'Hide manual testing' : 'Manual testing (local / no Cassidy)'}
        </button>
        {showPasteJson && (
          <div className="mt-2 space-y-3">
            {/* Step 1 — copy the prompt */}
            <div className="space-y-1.5">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold">Step 1 — Copy prompt and send to Cassidy or Claude</p>
              {selectedClient && selectedDiagram ? (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(buildPrompt(selectedClient.customerName, selectedDiagram.dsl))
                    setPromptCopied(true)
                    setTimeout(() => setPromptCopied(false), 2000)
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[11px] text-[var(--text-primary)] hover:border-violet-500/50 transition-colors"
                >
                  {promptCopied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  {promptCopied ? 'Prompt copied!' : `Copy prompt for ${selectedClient.customerName}`}
                </button>
              ) : (
                <p className="text-[11px] text-[var(--text-muted)] italic">Select a client and diagram above first.</p>
              )}
            </div>
            {/* Step 2 — paste the response */}
            <div className="space-y-1.5">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold">Step 2 — Paste the JSON response</p>
              <textarea
                value={pasteJsonValue}
                onChange={e => setPasteJsonValue(e.target.value)}
                placeholder={'Paste the JSON array from Cassidy/Claude here…\n[\n  { "id": "TC-01", "scenario": "...", ... }\n]'}
                rows={6}
                className="w-full text-[11px] font-mono bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y focus:outline-none focus:border-violet-500/50"
              />
              {pasteJsonError && <p className="text-[11px] text-red-400">{pasteJsonError}</p>}
              <Button variant="secondary" size="sm" onClick={loadFromPaste} disabled={!pasteJsonValue.trim()}>
                Load Test Cases
              </Button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-400">
          {error}
        </div>
      )}

      {cases.length > 0 && !credentials && (
        <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-400 flex items-center gap-1.5">
          <Square size={11} />
          Configure credentials in the API Console to enable automated test execution.
        </div>
      )}

      {progress && (
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-[var(--text-muted)]">{cases.length} test cases</span>
          <span className="text-emerald-400">{progress.pass} pass</span>
          <span className="text-red-400">{progress.fail} fail</span>
          {progress.blocked > 0 && <span className="text-amber-400">{progress.blocked} blocked</span>}
          <span className="text-[var(--text-muted)]">{progress.pending} pending</span>
          {progress.pending === 0 && (
            <span className="text-violet-400 font-semibold">Complete!</span>
          )}
        </div>
      )}

      {cases.length > 0 && (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border-color)] bg-violet-500/10">
            <Wand2 size={13} className="text-violet-400" />
            <span className="text-xs font-semibold text-violet-400">
              {selectedClient?.customerName} — UAT Test Cases
            </span>
            <span className="ml-auto text-[10px] text-[var(--text-muted)]">{cases.length} cases</span>
            {isRunning ? (
              <button
                onClick={stopAll}
                className="flex items-center gap-1 px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-[10px] text-red-400 hover:bg-red-500/20 transition-colors"
              >
                <Square size={9} /> Stop All
              </button>
            ) : (
              <button
                onClick={runAll}
                disabled={!credentials || isRunning}
                className="flex items-center gap-1 px-2 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-[10px] text-violet-400 hover:bg-violet-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Play size={9} /> Run All
              </button>
            )}
            <button
              onClick={() => exportToExcel(selectedClient?.customerName ?? 'UAT', cases, results)}
              className="text-[10px] text-[var(--text-muted)] hover:text-emerald-400 flex items-center gap-1 transition-colors"
            >
              <Download size={10} /> Export
            </button>
            <button
              onClick={() => { setCases([]); setError(null) }}
              className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center gap-1"
            >
              <RotateCcw size={10} /> Reset
            </button>
          </div>
          {runAllProgress && (
            <div className="px-3 py-1.5 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-muted)]">
                  Running {runAllProgress.current}/{runAllProgress.total}…
                </span>
                <div className="flex-1 h-1 rounded-full bg-[var(--bg-tertiary)] overflow-hidden">
                  <div
                    className="h-full bg-violet-500 rounded-full transition-all"
                    style={{ width: `${(runAllProgress.current / runAllProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--bg-tertiary)]">
                <th className="px-3 py-1.5 w-4" />
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide w-12">ID</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Scenario</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide hidden md:table-cell">Document</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Expected</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Result</th>
                <th className="px-2 py-1.5 w-16" />
              </tr>
            </thead>
            <tbody>
              {cases.map((tc, idx) => (
                <TestCaseRow
                  key={tc.id}
                  tc={tc}
                  idx={idx}
                  result={results[tc.id] ?? { actual: null, notes: '' }}
                  onResultChange={actual => updateResult(tc.id, actual)}
                  onNotesChange={notes => updateNotes(tc.id, notes)}
                  runState={runStates[tc.id]}
                  isAnyRunning={isRunning}
                  onRun={() => runCase(tc)}
                  onStop={() => stopCase(tc.id)}
                  manualWaitStepIdx={manualWait?.caseId === tc.id ? manualWait.stepIdx : undefined}
                  onManualDone={() => manualResolveRef.current?.()}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}
